import type { DealBookingType } from '@src/types/OTC';
import { logger } from '@talos/kyoko';
import { useEffect, useRef } from 'react';
import { getNewOTCMissingDependencyErrors } from './otcMissingDependencyTransitions';
import {
  useOTCMissingDependencies,
  type OTCMissingDependenciesLocation,
  type OTCMissingDependencyError,
} from './useOTCMissingDependencies';

type MonitorContext = {
  location: OTCMissingDependenciesLocation;
  bookingType: DealBookingType;
};

/**
 * Derives live OTC missing dependency errors and logs new failures / full recovery.
 * Returns a named render status for the banner.
 */
export function useMonitoredOTCMissingDependencies({
  location,
  bookingType,
}: {
  location: OTCMissingDependenciesLocation;
  bookingType: DealBookingType;
}): { hasMissingDependencies: boolean } {
  const errors = useOTCMissingDependencies();
  const previousRef = useRef<{
    context: MonitorContext | null;
    errors: OTCMissingDependencyError[];
  }>({ context: null, errors: [] });

  useEffect(() => {
    const previous = previousRef.current;
    const contextChanged =
      previous.context == null ||
      previous.context.location !== location ||
      previous.context.bookingType !== bookingType;

    if (contextChanged) {
      previousRef.current = { context: { location, bookingType }, errors };

      if (errors.length > 0) {
        logger.error(new Error('OTC: missing required dependencies'), {
          extra: {
            location,
            bookingType,
            newErrors: errors,
            allErrors: errors,
          },
        });
      }
      return;
    }

    const newErrors = getNewOTCMissingDependencyErrors(previous.errors, errors);

    if (previous.errors.length > 0 && errors.length === 0) {
      logger.info('OTC: required dependencies recovered', {
        extra: {
          location,
          bookingType,
        },
      });
    }

    if (newErrors.length > 0) {
      logger.error(new Error('OTC: missing required dependencies'), {
        extra: {
          location,
          bookingType,
          newErrors,
          allErrors: errors,
        },
      });
    }

    previousRef.current = { context: { location, bookingType }, errors };
  }, [location, bookingType, errors]);

  return { hasMissingDependencies: errors.length > 0 };
}
